import React, {
  useState, useRef, useCallback, useEffect, useMemo,
} from 'react';
import ReactDOM from 'react-dom';
import { getShapePositionByScreenBound } from '@meitu/whee-infinite-canvas';
import IconFont from '$/components/IconFont';
import useAIToolsCanvasStore from '@/store/useAIToolsCanvasStore';
import { useShallow } from 'zustand/react/shallow';
import styles from './index.module.scss';

/** 画布缩放低于该百分比时不展示控制条 */
const MIN_ZOOM_PERCENT_TO_SHOW_BAR = 5;

const BAR_BOTTOM_OFFSET = 8;

/**
 * 隐藏画布视频对象内置的右上角播放图标，播放只保留底部控制条入口。
 */
function hideCanvasVideoIcon(videoObject: any, render: any) {
  const icon = videoObject?.IConImage;
  if (!icon) return;

  const isVisible = typeof icon.get === 'function' ? icon.get('visible') : icon.visible;
  if (isVisible === false) return;

  icon.set?.('visible', false);
  if (typeof render?._FC?.requestRenderAll === 'function') {
    render._FC.requestRenderAll();
  } else {
    render?._FC?.renderAll?.();
  }
}

/**
 * 将画布视频对象恢复到封面态，避免没有可绘制视频帧时出现黑屏。
 */
function restoreCanvasVideoCover(videoObject: any, render: any) {
  videoObject?.fabricImage?.set?.('visible', true);
  videoObject?.fabricVideo?.set?.('visible', false);
  hideCanvasVideoIcon(videoObject, render);
}

/**
 * 将秒数格式化成控制条展示的 mm:ss 文案。
 */
function formatTime(seconds: number) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

export default function VideoBar() {
  const { render, imageTarget, zoomNum } = useAIToolsCanvasStore(useShallow((state) => ({
    render: state.render,
    imageTarget: state.imageTarget,
    zoomNum: state.zoomNum,
  })));

  const videoObject = imageTarget as any;
  const videoElement = videoObject?.videoElement as HTMLVideoElement | undefined;

  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [barLeft, setBarLeft] = useState(0);
  const [barRight, setBarRight] = useState(0);
  const [barTop, setBarTop] = useState(0);
  const [barBottom, setBarBottom] = useState(0);
  const [showVideoOverlay, setShowVideoOverlay] = useState(false);

  const dragTime = useRef(0);
  const overlayVideoRef = useRef<HTMLVideoElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const progressThumbRef = useRef<HTMLDivElement>(null);
  const progressFillRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const dragPercentage = useRef(0);
  const progressPercentage = useRef(0);
  const videoSrc = useMemo(() => (
    videoElement?.currentSrc
      || videoElement?.src
      || videoObject?.sourceSrc
      || videoObject?.source
      || ''
  ), [videoElement, videoObject]);

  // 计算视频元素在屏幕上的位置
  const updateBarPosition = useCallback(() => {
    if (!render || !videoObject) return;
    const data = getShapePositionByScreenBound.call(render, videoObject);
    if (data) {
      setBarLeft(data.left);
      setBarRight(data.right);
      setBarTop(data.top);
      setBarBottom(data.bottom);
      hideCanvasVideoIcon(videoObject, render);
    }
  }, [render, videoObject]);

  // 监听画布缩放/平移更新位置
  useEffect(() => {
    if (!render || !videoObject) return undefined;
    updateBarPosition();
    render._FC.on('viewport:zoom', updateBarPosition);
    render._FC.on('after:render', updateBarPosition);
    return () => {
      render._FC.off('viewport:zoom', updateBarPosition);
      render._FC.off('after:render', updateBarPosition);
    };
  }, [render, videoObject, updateBarPosition]);

  /**
   * 右上角播放图标是异步创建的，首帧多等几次确保它出现后也被隐藏。
   */
  useEffect(() => {
    if (!render || !videoObject) return undefined;
    const timerIds = [0, 100, 300, 800].map((delay) => window.setTimeout(() => {
      hideCanvasVideoIcon(videoObject, render);
    }, delay));
    return () => {
      timerIds.forEach((timerId) => window.clearTimeout(timerId));
    };
  }, [render, videoObject]);

  const barStyle = useMemo(() => {
    const width = barRight - barLeft;
    return {
      left: `${(barRight - barLeft) / 2 + barLeft}px`,
      top: `${barBottom + BAR_BOTTOM_OFFSET}px`,
      transform: 'translate(-50%, 0)',
      width: width < 200 ? 200 : width,
    };
  }, [barLeft, barRight, barBottom]);

  const overlayStyle = useMemo(() => ({
    left: `${barLeft}px`,
    top: `${barTop}px`,
    width: `${Math.max(0, barRight - barLeft)}px`,
    height: `${Math.max(0, barBottom - barTop)}px`,
    opacity: showVideoOverlay ? 1 : 0,
  }), [barBottom, barLeft, barRight, barTop, showVideoOverlay]);

  /**
   * 直接同步进度条 DOM，避免播放过程频繁 setState 造成控制条卡顿。
   */
  const updateProgressDOM = useCallback((time: number, videoDuration: number) => {
    if (videoDuration <= 0) return;
    const pct = Math.min(100, Math.max(0, (time / videoDuration) * 100));
    progressPercentage.current = pct;
    if (!isDraggingRef.current) {
      if (progressThumbRef.current) progressThumbRef.current.style.left = `${pct}%`;
      if (progressFillRef.current) progressFillRef.current.style.width = `${pct}%`;
    }
  }, []);

  /**
   * 监听真实播放元素的媒体事件，同步底部控制条和封面/视频层状态。
   */
  useEffect(() => {
    const playbackElement = overlayVideoRef.current || videoElement;
    if (!playbackElement) return undefined;

    const onLoadedMetadata = () => {
      const d = Math.round(playbackElement.duration || 0);
      setDuration(d);
      updateProgressDOM(
        playbackElement.currentTime === d ? 0 : playbackElement.currentTime,
        d,
      );
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => {
      setIsPlaying(false);
      if ((playbackElement.currentTime || 0) === 0) {
        setShowVideoOverlay(false);
        restoreCanvasVideoCover(videoObject, render);
      }
    };
    const onTimeUpdate = () => {
      if (isDraggingRef.current) return;
      const ct = playbackElement.currentTime || 0;
      const d = playbackElement.duration || 0;
      if (ct >= d && d > 0) {
        playbackElement.currentTime = 0;
        setCurrentTime(0);
        setShowVideoOverlay(false);
        restoreCanvasVideoCover(videoObject, render);
        updateProgressDOM(0, d);
      } else {
        setCurrentTime(ct);
        updateProgressDOM(ct, d);
      }
    };
    const onEnded = () => {
      setIsPlaying(false);
      playbackElement.currentTime = 0;
      setCurrentTime(0);
      setShowVideoOverlay(false);
      restoreCanvasVideoCover(videoObject, render);
      updateProgressDOM(0, playbackElement.duration || 0);
    };

    playbackElement.addEventListener('loadedmetadata', onLoadedMetadata);
    playbackElement.addEventListener('play', onPlay);
    playbackElement.addEventListener('pause', onPause);
    playbackElement.addEventListener('timeupdate', onTimeUpdate);
    playbackElement.addEventListener('ended', onEnded);

    const d = Math.round(playbackElement.duration || 0);
    setDuration(d);
    setCurrentTime(playbackElement.currentTime || 0);
    setIsPlaying(!playbackElement.paused);
    updateProgressDOM(
      playbackElement.currentTime === d ? 0 : playbackElement.currentTime,
      d,
    );

    return () => {
      playbackElement.removeEventListener('loadedmetadata', onLoadedMetadata);
      playbackElement.removeEventListener('play', onPlay);
      playbackElement.removeEventListener('pause', onPause);
      playbackElement.removeEventListener('timeupdate', onTimeUpdate);
      playbackElement.removeEventListener('ended', onEnded);
    };
  }, [render, videoElement, videoObject, videoSrc, updateProgressDOM]);

  /**
   * 当前时间变更时补齐进度条位置，拖拽过程由 DOM 自己接管。
   */
  useEffect(() => {
    if (progressFillRef.current && duration > 0) {
      updateProgressDOM(currentTime === duration ? 0 : currentTime, duration);
    }
  }, [duration, currentTime, updateProgressDOM]);

  /**
   * 视频停在起始帧时保持封面态，避免兜底播放失败后画布只剩黑底。
   */
  useEffect(() => {
    const playbackElement = overlayVideoRef.current;
    if (!playbackElement || !videoObject) return;
    if (playbackElement.paused && (playbackElement.currentTime || 0) === 0) {
      restoreCanvasVideoCover(videoObject, render);
    }
  }, [render, videoObject, videoSrc]);

  /**
   * 播放真实 DOM video，避开底层画布离屏视频在用户手势外卡住的问题。
   */
  const handlePlayPause = useCallback(async () => {
    const playbackElement = overlayVideoRef.current;
    if (!videoObject || !playbackElement) return;
    if (isPlaying) {
      playbackElement.pause();
      videoObject.pause?.();
      return;
    }

    try {
      if (duration > 0 && playbackElement.currentTime >= duration) {
        playbackElement.currentTime = 0;
      }
      videoObject.pause?.();
      await playbackElement.play();
      setShowVideoOverlay(true);
    } catch (error) {
      setShowVideoOverlay(false);
      restoreCanvasVideoCover(videoObject, render);
      console.error('视频播放失败:', error);
    }
  }, [duration, isPlaying, render, videoObject]);

  /**
   * 在 pointer 阶段响应播放按钮，避免底层画布拖拽逻辑吞掉最终 click。
   */
  const handlePlayButtonPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handlePlayPause().catch((error) => {
      console.error('视频播放失败:', error);
    });
  }, [handlePlayPause]);

  /**
   * 点击进度条时记录目标时间，并立即移动 thumb/进度填充。
   */
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isDraggingRef.current = true;
    setIsDragging(true);
    const playbackElement = overlayVideoRef.current;
    if (!progressBarRef.current || !playbackElement) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const newTime = pct * (playbackElement.duration || 0);
    if (progressThumbRef.current) progressThumbRef.current.style.left = `${pct * 100}%`;
    if (progressFillRef.current) progressFillRef.current.style.width = `${pct * 100}%`;
    dragTime.current = newTime;
    dragPercentage.current = pct * 100;
  }, []);

  /**
   * 拖动进度条时只更新临时位置，等松手后再 seek 到真实视频时间。
   */
  const handleMouseMove = useCallback((e: MouseEvent) => {
    const playbackElement = overlayVideoRef.current;
    if (!isDraggingRef.current || !progressBarRef.current || !playbackElement) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const val = pct * 100;
    if (progressThumbRef.current) progressThumbRef.current.style.left = `${val}%`;
    if (progressFillRef.current) progressFillRef.current.style.width = `${val}%`;
    dragPercentage.current = val;
    dragTime.current = pct * (playbackElement.duration || 0);
  }, []);

  /**
   * 松开拖拽后提交 seek，并在非起点时展示真实视频层。
   */
  const handleMouseUp = useCallback(() => {
    const playbackElement = overlayVideoRef.current;
    if (isDraggingRef.current && playbackElement) {
      playbackElement.currentTime = dragTime.current;
      if (dragTime.current > 0) setShowVideoOverlay(true);
      isDraggingRef.current = false;
      setIsDragging(false);
      progressPercentage.current = dragPercentage.current;
      setCurrentTime(dragTime.current);
    }
  }, []);

  /**
   * 从 thumb 开始拖拽时阻止事件冒泡，避免进度条重复计算起点。
   */
  const handleThumbMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    isDraggingRef.current = true;
    setIsDragging(true);
    dragTime.current = currentTime;
  }, [currentTime]);

  /**
   * 仅在拖拽期间绑定全局鼠标事件，保证移出控制条后仍能完成 seek。
   */
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return undefined;
  }, [isDragging, handleMouseMove, handleMouseUp]);

  /**
   * 非拖拽状态下把临时时间对齐到当前播放时间。
   */
  useEffect(() => {
    if (!isDragging) {
      dragTime.current = currentTime;
    }
  }, [currentTime, isDragging]);

  if (!videoObject || videoObject.frameType !== 'video') return null;
  // 缩放过小不展示视频控制条
  if (zoomNum < MIN_ZOOM_PERCENT_TO_SHOW_BAR) return null;

  return ReactDOM.createPortal(
    <>
      {videoSrc && (
        <video
          ref={overlayVideoRef}
          className={styles.videoOverlay}
          style={overlayStyle}
          src={videoSrc}
          playsInline
          preload="metadata"
        >
          <track kind="captions" />
        </video>
      )}
      <div className={styles.videoBar} style={barStyle}>
        <div className={styles.playButton} onPointerDown={handlePlayButtonPointerDown}>
          <IconFont
            name={isPlaying ? 'pauseFill' : 'playFill'}
            fontSize={16}
            color="#111214"
          />
        </div>

        {/* 进度条 */}
        <div className={`${styles.progressContainer} ${isDragging ? styles.dragging : ''}`}>
          <div
            className={styles.progressBar}
            ref={progressBarRef}
            onMouseDown={handleMouseDown}
          >
            <div className={styles.progressFill} ref={progressFillRef} />
          </div>
          <div
            className={styles.progressThumb}
            ref={progressThumbRef}
            onMouseDown={handleThumbMouseDown}
          />
        </div>

        {/* 时间显示 */}
        <div className={styles.timeDisplay}>
          <span className={styles.currentTime}>{formatTime(currentTime)}</span>
          <span className={styles.duration}>
            /
            {formatTime(duration)}
          </span>
        </div>
      </div>
    </>,
    document.body,
  );
}
